<script>
import { MOLDURA_DO_THREAD } from '@modules/aawz-intelligence-v2/ui-registry/moldura-do-thread'
import { ARQUIVO_DO_TURNO } from '@modules/aawz-intelligence-v2/constants/Text.busca'
import {
  TIPO_DE_ARTEFATO,
  tipoDeArtefato,
} from '@modules/aawz-intelligence-v2/utils/tipo-de-artefato'
import { fileSize } from '@utils/Format'

/**
 * [spec-003 T301 / US-4, US-5, US-6] O ÍCONE de cada família de artefato.
 *
 * Um MAPA, e não uma cadeia de `if` no template: a condição que escolhe o
 * glifo mora num lugar só, porque é ela que o teste de mutação troca. Todos
 * os nomes EXISTEM em `src/components/icons/svg/` — `icon-svg` com nome
 * inexistente renderiza VAZIO, sem erro nenhum.
 */
const ICONE_POR_FAMILIA = Object.freeze({
  [TIPO_DE_ARTEFATO.PLANILHA]: 'sheet',
  [TIPO_DE_ARTEFATO.DOCUMENTO]: 'document-text',
  [TIPO_DE_ARTEFATO.GRAFICO]: 'chart-bar',
  [TIPO_DE_ARTEFATO.ARQUIVO]: 'document-simple',
})

/**
 * Tamanho legível — string vazia quando não dá para afirmar nada.
 *
 * ⚠️ `fileSize(0)` devolve `NaN B`: `Math.log(0)` é `-Infinity`. O contrato
 * permite `bytes: 0` (`minimum: 0`), então o guard não é hipotético.
 *
 * @param {Number} bytes O `bytes` do payload.
 * @returns {String} O tamanho formatado, ou vazio.
 */
function tamanhoLegivel(bytes) {
  if (typeof bytes !== 'number' || !isFinite(bytes) || bytes <= 0) return ''
  return fileSize(bytes)
}

export default {
  name: 'IntelligenceV2ArquivoGerado',
  props: {
    // Os payloads `d_arquivo` CRUS, na ordem de chegada (getter
    // `arquivoStreaming`). METADADO, nunca bytes — ver `SEM_DOWNLOAD_AQUI`.
    eventos: {
      type: Array,
      default: function () {
        return []
      },
    },
  },
  data() {
    return {
      MOLDURA_DO_THREAD,
      ARQUIVO_DO_TURNO,
      aberto: true,
    }
  },
  computed: {
    temArquivos() {
      return this.eventos.length > 0
    },
    resumo() {
      return String(this.eventos.length)
    },
    classeSeta() {
      return this.aberto ? 'ia-block-seta--aberta' : ''
    },
    itens() {
      return this.eventos.map(decorarArquivo)
    },
  },
  methods: {
    alternar() {
      this.aberto = !this.aberto
    },
  },
}

/**
 * Um `d_arquivo` → a linha pronta para o template.
 *
 * @param {Object} evento O payload do evento `arquivo`.
 * @param {Number} indice Posição no turno.
 * @returns {Object} A linha decorada.
 */
function decorarArquivo(evento, indice) {
  const tamanho = tamanhoLegivel(evento.bytes)
  const versao = evento.versao > 1 ? ARQUIVO_DO_TURNO.VERSAO(evento.versao) : ''
  // [T301] A FAMÍLIA é o que o consultor lê — planilha, documento, gráfico.
  // O `tipo` do contrato (`anexo|artefato|manifesto`) diz de ONDE o arquivo
  // veio, e só aparece quando NÃO é artefato: escrever "artefato · planilha"
  // em toda linha gastaria a legenda com a palavra menos informativa das duas.
  const familia = tipoDeArtefato(evento)
  const rotuloDaFamilia = ARQUIVO_DO_TURNO.FAMILIA[familia] || ''
  const procedencia =
    evento.tipo && evento.tipo !== 'artefato' ? ARQUIVO_DO_TURNO.TIPO[evento.tipo] || '' : ''
  return {
    // `id_objeto` é o identificador do contrato; o índice só desempata quando
    // ele não veio (`:key` duplicada gera warning e erro de atualização).
    chave: (evento.id_objeto || evento.chave || 'arquivo') + '-' + indice,
    // O NOME é o que o consultor reconhece; a chave é caminho interno.
    nome: evento.nome || evento.chave || '',
    icone: ICONE_POR_FAMILIA[familia] || ICONE_POR_FAMILIA[TIPO_DE_ARTEFATO.ARQUIVO],
    // Uma linha só: `planilha · 12.4 kB · versão 2` (ou `anexo · documento · 1.2 MB`).
    legenda: [procedencia, rotuloDaFamilia, tamanho, versao].filter(Boolean).join(' · '),
  }
}
</script>

<template>
  <div v-if="temArquivos" class="arquivo-gerado ia-block-card" v-bind="MOLDURA_DO_THREAD">
    <button
      type="button"
      class="ia-block-card__cabecalho ia-block-card__cabecalho--fita ia-block-card__cabecalho--clicavel"
      :aria-expanded="String(aberto)"
      :aria-label="ARQUIVO_DO_TURNO.ARIA_LISTA"
      data-test="arquivo-gerado-cabecalho"
      @click="alternar"
    >
      <icon-svg
        name="document-text"
        :size="14"
        color="var(--primary)"
        class="ia-block-card__icone"
      />
      <span class="ia-block-card__titulo">{{ ARQUIVO_DO_TURNO.TITULO }}</span>
      <span class="ia-block-card__meta">{{ resumo }}</span>
      <icon-svg
        name="arrow-down-new-ux"
        :size="14"
        color="var(--neutral-700)"
        class="ia-block-seta"
        :class="classeSeta"
      />
    </button>

    <div v-show="aberto" class="arquivo-gerado__corpo">
      <div
        v-for="item in itens"
        :key="item.chave"
        class="arquivo-gerado__item"
        data-test="arquivo-gerado-item"
      >
        <icon-svg :name="item.icone" :size="14" color="var(--neutral-700)" />
        <span class="arquivo-gerado__nome ia-texto-auxiliar">{{ item.nome }}</span>
        <span v-if="item.legenda" class="arquivo-gerado__legenda ia-texto-auxiliar">
          {{ item.legenda }}
        </span>
      </div>

      <!-- O evento é METADADO, sem bytes. Quem baixa é o bloco de Entregas da
           bolha persistida (`chat-message.vue::entregasDoTurno`, spec-008
           T028): um segundo botão de download aqui pediria os bytes que este
           payload não tem, e duplicaria a lista que já existe. -->
      <p class="arquivo-gerado__nota ia-texto-auxiliar">{{ ARQUIVO_DO_TURNO.SEM_DOWNLOAD_AQUI }}</p>
    </div>
  </div>
</template>

<style lang="sass" scoped>
.arquivo-gerado
  margin-top: 12px

// Reset do `button` do cabeçalho (ver `busca-web.vue`).
.ia-block-card__cabecalho
  width: 100%
  text-align: left
  font: inherit
  border: 0

.ia-block-card__cabecalho--fita
  border-bottom: 1px solid var(--border-hairline)

.arquivo-gerado__corpo
  display: flex
  flex-direction: column
  gap: 6px
  padding: 10px 14px

.arquivo-gerado__item
  display: flex
  align-items: center
  gap: 8px
  min-width: 0

.arquivo-gerado__nome
  color: var(--neutral-900)
  flex: 1 1 auto
  min-width: 0
  // Nome longo trunca em vez de empurrar a legenda para fora do card.
  overflow: hidden
  text-overflow: ellipsis
  white-space: nowrap

.arquivo-gerado__legenda
  color: var(--neutral-700)
  flex-shrink: 0

.arquivo-gerado__nota
  color: var(--neutral-700)
  margin: 0
</style>
